/**
 * Shared vocabulary for the Linear-style work-order views (#603): the
 * status, priority and type sets, and how a work-order list is ordered,
 * filtered and grouped. The work-order counterpart of violationMeta.
 *
 * Pure functions only, so the rules are unit-testable.
 */
import { WorkOrderCreate, WorkOrderReturn } from '../../clients/rust-api-client';
import { noonUtcMillis, utcDay } from '../../violation/linear/violationMeta';

export type WorkOrderStatus =
  | 'InProgress'
  | 'Scheduled'
  | 'Approved'
  | 'Pending'
  | 'Completed'
  | 'Cancelled';

/**
 * Display order, which is also group order in the list: work under way
 * first, then what is lined up, then what still needs a decision; closed
 * work last. Values are the API's (`WorkOrderStatus` on the backend).
 */
export const STATUSES: { value: WorkOrderStatus; label: string }[] = [
  { value: 'InProgress', label: 'In Progress' },
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

export const PRIORITIES = ['Urgent', 'High', 'Medium', 'Low'] as const;

/** The create form's values; the API stores what it was sent. */
export const WORK_ORDER_TYPES = [
  'maintenance',
  'repair',
  'improvement',
  'inspection',
  'landscaping',
  'cleaning',
  'other',
] as const;

const key = (s: string) => s.toLowerCase().replace(/[_ ]/g, '');

/** Whatever casing the API or a caller used: 'in_progress' → 'InProgress'. */
export const normalizeStatus = (status: string): WorkOrderStatus | null =>
  STATUSES.find((s) => key(s.value) === key(status))?.value ?? null;

/** 'InProgress' / 'in_progress' → 'In Progress'; unknown values title-cased. */
export const statusLabel = (status: string): string => {
  const s = normalizeStatus(status);
  if (s) return STATUSES.find((x) => x.value === s)!.label;
  return titleCase(status.replace(/([a-z])([A-Z])/g, '$1 $2'));
};

/** 'landscaping' → 'Landscaping', 'high' → 'High'. */
export const titleCase = (value: string): string =>
  value
    .split(/[_ ]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

export const isOpen = (w: WorkOrderReturn): boolean => {
  const s = normalizeStatus(w.status);
  return s !== 'Completed' && s !== 'Cancelled';
};

/** 0 for Urgent … 3 for Low; unknown priorities sort last. */
export const priorityRank = (priority: string): number => {
  const i = PRIORITIES.findIndex((p) => key(p) === key(priority));
  return i === -1 ? PRIORITIES.length : i;
};

/** Priority on the violation severity scale, for the shared bar glyph. */
export const priorityAsSeverity = (priority: string): string =>
  ['Critical', 'Major', 'Moderate', 'Minor'][priorityRank(priority)] ?? '';

/** A short, stable reference for a work order — the tail of its public id. */
export const shortRef = (id: string): string =>
  `WO-${id.replace(/^wo_/, '').slice(0, 5).toUpperCase()}`;

/** True when the work order is open and its scheduled day has passed. */
export const isPastScheduled = (
  w: WorkOrderReturn,
  now: Date = new Date()
): boolean => {
  const day = utcDay(w.scheduled_date);
  return !!day && isOpen(w) && day < now.toISOString().slice(0, 10);
};

/** Most urgent first, then soonest scheduled (unscheduled last), then newest. */
export const byUrgency = (a: WorkOrderReturn, b: WorkOrderReturn): number => {
  const pa = priorityRank(a.priority);
  const pb = priorityRank(b.priority);
  if (pa !== pb) return pa - pb;
  const sa = utcDay(a.scheduled_date);
  const sb = utcDay(b.scheduled_date);
  if (sa !== sb) {
    if (!sa) return 1;
    if (!sb) return -1;
    return sa < sb ? -1 : 1;
  }
  return utcDay(b.date_created).localeCompare(utcDay(a.date_created));
};

// ---------------------------------------------------------------------------
// Filtering and grouping
// ---------------------------------------------------------------------------

export interface WorkOrderFilters {
  statuses: WorkOrderStatus[];
  priorities: string[];
  types: string[];
  /** HOA ids (#618). */
  hoas: string[];
  search: string;
}

export const EMPTY_FILTERS: WorkOrderFilters = {
  statuses: [],
  priorities: [],
  types: [],
  hoas: [],
  search: '',
};

/** Every non-empty filter must match ("match all filters"). */
export const applyFilters = (
  workOrders: WorkOrderReturn[],
  filters: WorkOrderFilters
): WorkOrderReturn[] => {
  const q = filters.search.trim().toLowerCase();
  return workOrders.filter((w) => {
    const status = normalizeStatus(w.status);
    if (filters.statuses.length && (!status || !filters.statuses.includes(status)))
      return false;
    if (
      filters.priorities.length &&
      !filters.priorities.some((p) => key(p) === key(w.priority))
    )
      return false;
    if (
      filters.types.length &&
      !filters.types.some((t) => key(t) === key(w.work_order_type))
    )
      return false;
    if (filters.hoas.length && !filters.hoas.includes(w.hoa_id)) return false;
    if (
      q &&
      ![w.title, w.description, w.work_order_type, w.vendor_name ?? '']
        .some((field) => field.toLowerCase().includes(q))
    )
      return false;
    return true;
  });
};

export interface WorkOrderGroup {
  status: WorkOrderStatus | 'Other';
  label: string;
  workOrders: WorkOrderReturn[];
}

/**
 * Groups by status in STATUSES order, most urgent first inside each group.
 * Empty groups are dropped; a status the UI does not know lands in a
 * trailing "Other" group rather than vanishing.
 */
export const groupByStatus = (
  workOrders: WorkOrderReturn[]
): WorkOrderGroup[] => {
  const sorted = [...workOrders].sort(byUrgency);
  const groups: WorkOrderGroup[] = STATUSES.map((s) => ({
    status: s.value,
    label: s.label,
    workOrders: sorted.filter((w) => normalizeStatus(w.status) === s.value),
  }));
  const other = sorted.filter((w) => normalizeStatus(w.status) === null);
  if (other.length)
    groups.push({ status: 'Other', label: 'Other', workOrders: other });
  return groups.filter((g) => g.workOrders.length > 0);
};

// ---------------------------------------------------------------------------
// Create (#632)
// ---------------------------------------------------------------------------

/** What the create modal holds, before it becomes a request. */
export interface WorkOrderDraft {
  hoaId: string | null;
  homeId: string | null;
  title: string;
  description: string;
  type: string | null;
  priority: string;
  status: WorkOrderStatus;
  /** 'YYYY-MM-DD' or ''. */
  scheduled: string;
  vendorId: string | null;
  vendorName: string | null;
  assignedTo: string;
  /** Dollars as typed, '' for none. */
  estimatedCost: string;
  notes: string;
}

export const TITLE_MAX = 200;
export const DESCRIPTION_MAX = 1000;
export const ESTIMATE_MAX = 1_000_000;
const COST_PATTERN = /^\d{0,7}(\.\d{0,2})?$/;

/** '' or dollars and cents, no more than ESTIMATE_MAX. */
export const isValidEstimate = (value: string): boolean => {
  const v = value.trim();
  return v === '' || (v !== '.' && COST_PATTERN.test(v) && Number(v) <= ESTIMATE_MAX);
};

/** What still stops the draft from being created, phrased for "Add …". */
export const missingForWorkOrder = (d: WorkOrderDraft): string[] =>
  [
    !d.hoaId && 'an association',
    !d.title.trim() && 'a title',
    !d.description.trim() && 'a description',
    !d.type && 'a type',
  ].filter(Boolean) as string[];

/**
 * The request for a complete draft, or null. The scheduled day goes as
 * epoch **milliseconds** at noon UTC — the API reads every date as
 * milliseconds, and a raw 'YYYY-MM-DD' is a 400 (#606). Empty optional
 * fields go as null, not as empty strings.
 */
export const buildWorkOrderCreate = (d: WorkOrderDraft): WorkOrderCreate | null => {
  if (missingForWorkOrder(d).length || !d.hoaId || !d.type) return null;
  if (d.title.trim().length > TITLE_MAX) return null;
  if (d.description.trim().length > DESCRIPTION_MAX) return null;
  if (!isValidEstimate(d.estimatedCost)) return null;
  const scheduled = d.scheduled ? noonUtcMillis(d.scheduled) : null;
  if (d.scheduled && scheduled === null) return null;
  const text = (v: string | null) => (v && v.trim() ? v.trim() : null);
  const cost = d.estimatedCost.trim();
  return {
    hoa_id: d.hoaId,
    home_id: d.homeId,
    title: d.title.trim(),
    description: d.description.trim(),
    work_order_type: d.type,
    priority: d.priority,
    status: d.status,
    scheduled_date: scheduled,
    vendor_id: d.vendorId,
    vendor_name: text(d.vendorName),
    assigned_to: text(d.assignedTo),
    estimated_cost: cost && Number(cost) > 0 ? cost : null,
    notes: text(d.notes),
  };
};
